/**
 * Sentry Error Tracking & Performance Monitoring
 *
 * Initialises Sentry with browser tracing, session replay, and custom context.
 * Safe to import anywhere — all calls are no-ops when the DSN is missing.
 */

import * as Sentry from '@sentry/react'

const dsn = import.meta.env.VITE_SENTRY_DSN

if (dsn) {
  // Build integrations list — some may not be available in test/SSR environments
  const integrations: NonNullable<Parameters<typeof Sentry.init>[0]>['integrations'] = []
  if (typeof Sentry.browserTracingIntegration === 'function') {
    integrations.push(Sentry.browserTracingIntegration())
  }
  if (typeof Sentry.replayIntegration === 'function') {
    integrations.push(Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }))
  }
  if (typeof Sentry.breadcrumbsIntegration === 'function') {
    integrations.push(Sentry.breadcrumbsIntegration({
      console: true, dom: true, fetch: true, history: true, xhr: true,
    }))
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || undefined,
    integrations,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.2),
    replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE ?? 0.0),
    replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE ?? 1.0),
    sendDefaultPii: false,
    // Ignore common non-actionable errors
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection captured',
      'Network request failed',
      'Load failed',
      'Failed to fetch',
    ],
    beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
      // Strip Supabase anon key from URLs in breadcrumbs
      if (event.breadcrumbs) {
        for (const crumb of event.breadcrumbs) {
          if (crumb.data?.url && typeof crumb.data.url === 'string') {
            crumb.data.url = crumb.data.url.replace(/apikey=[^&]+/, 'apikey=[REDACTED]')
          }
        }
      }

      // Convert raw Supabase PostgrestError objects into proper Error messages.
      // Without this, Sentry shows "Object captured as exception with keys: code, details, hint, message"
      const original = hint?.originalException
      if (original && typeof original === 'object' && !(original instanceof Error) && 'message' in original) {
        const pgErr = original as { message: string; code?: string }
        // Enrich the event title with the actual error message
        if (event.exception?.values?.[0]) {
          event.exception.values[0].value = pgErr.message
          event.exception.values[0].type = `PostgrestError${pgErr.code ? ` (${pgErr.code})` : ''}`
        }
      }

      return event
    },
  })
}

/**
 * Set user context after login.
 */
export function setSentryUser(user: {
  id: string
  email?: string
  organization_id?: string
}) {
  if (!dsn) return
  Sentry.setUser({
    id: user.id,
    email: user.email,
  })
  if (user.organization_id) {
    Sentry.setTag('organization_id', user.organization_id)
  }
}

/**
 * Clear user context on logout.
 */
export function clearSentryUser() {
  if (!dsn) return
  Sentry.setUser(null)
}

/**
 * Capture an exception with optional extra context.
 */
export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (!dsn) {
    console.error(error)
    return
  }
  Sentry.captureException(error, { extra: context })
}

/**
 * Add a breadcrumb for debugging.
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
) {
  if (!dsn) return
  Sentry.addBreadcrumb({ message, category, data, level: 'info' })
}

export { Sentry }
